/**
 * useUserLanguage Hook
 *
 * Custom hook for managing user language preferences
 * Integrates with Jotai atoms, i18next, localStorage, and Firestore
 *
 * Flow: localStorage -> Firestore -> Browser -> Fallback to 'en'
 */

import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import type { TFunction, i18n as I18nInstance } from 'i18next';
import { useOptionalAuth } from '@/contexts/AuthProvider';
import {
  currentLanguageAtom,
  languagePreferenceAtom,
  languageChangeEffectAtom,
} from '@/stores/atoms';
import {
  getLanguagePreference,
  syncLanguagePreference,
  handleLanguageChange,
} from '@/utils/language';
import { SupportedLanguage, changeLanguage, onLanguageChange } from '@/i18n';
import { logger } from '@/lib/logger';

/**
 * Hook for managing user language preferences
 * @returns Object with language state and methods
 */
export function useUserLanguage(): {
  currentLanguage: string;
  languagePreference: string;
  isInitialized: boolean;
  changeLanguage: (newLanguage: SupportedLanguage) => Promise<void>;
  syncLanguagePreference: () => Promise<void>;
  getAvailableLanguages: () => Array<{
    code: SupportedLanguage;
    name: string;
    nativeName: string;
  }>;
  isLanguageSupported: (language: string) => language is SupportedLanguage;
  i18n: I18nInstance;
  t: TFunction;
} {
  const auth = useOptionalAuth();
  const userId = auth?.user?.userId; // Undefined if not authenticated or auth not ready

  const { i18n } = useTranslation();

  // Jotai atoms
  const [currentLanguage, setCurrentLanguage] = useAtom(currentLanguageAtom);
  const [languagePreference, setLanguagePreference] = useAtom(languagePreferenceAtom);
  const triggerLanguageChange = useSetAtom(languageChangeEffectAtom);

  // Initialize language on mount
  useEffect(() => {
    const initializeLanguage = async (): Promise<void> => {
      try {
        // Get language preference with fallback chain:
        // localStorage -> Firestore -> browser -> default
        const preferredLanguage = await getLanguagePreference(userId);

        // Update if language is different from current
        if (i18n.language !== preferredLanguage) {
          // Update i18next
          await changeLanguage(preferredLanguage);

          // Update atoms
          setLanguagePreference(preferredLanguage);
          setCurrentLanguage(preferredLanguage);
        } else {
          // Update atoms to sync state
          setLanguagePreference(preferredLanguage);
          setCurrentLanguage(preferredLanguage);
        }
      } catch (error) {
        logger.warn('Failed to initialize language', { error });
        // Fallback to default language
        const defaultLang = 'en' as SupportedLanguage;
        setLanguagePreference(defaultLang);
        setCurrentLanguage(defaultLang);
        if (i18n.language !== defaultLang) {
          await changeLanguage(defaultLang);
        }
      }
    };

    initializeLanguage();
  }, [userId, setLanguagePreference, setCurrentLanguage, i18n]);

  // Listen for i18next language changes
  useEffect(() => {
    const cleanup = onLanguageChange((newLanguage: SupportedLanguage) => {
      // Update atoms when i18next language changes
      setLanguagePreference(newLanguage);
      setCurrentLanguage(newLanguage);
    });

    return cleanup;
  }, [setLanguagePreference, setCurrentLanguage]);

  // Change language function
  const changeUserLanguage = async (newLanguage: SupportedLanguage): Promise<void> => {
    try {
      // Update i18next
      await changeLanguage(newLanguage);

      // Update atoms
      setCurrentLanguage(newLanguage);
      setLanguagePreference(newLanguage);

      // Trigger language change effect
      triggerLanguageChange(newLanguage);

      // Sync with Firestore if user is authenticated
      if (userId) {
        await handleLanguageChange(userId, newLanguage);
      }
    } catch (error) {
      logger.error('Failed to change language', { error, newLanguage });
      throw error;
    }
  };

  // Sync language preference with Firestore
  const syncLanguagePreferenceToFirestore = async (): Promise<void> => {
    try {
      // Sync to Firestore if user is authenticated
      if (userId) {
        await syncLanguagePreference(userId, currentLanguage);
      }
    } catch (error) {
      logger.warn('Failed to sync language preference', { error, currentLanguage });
    }
  };

  // Get available languages
  const getAvailableLanguages = (): Array<{
    code: SupportedLanguage;
    name: string;
    nativeName: string;
  }> => {
    return [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
    ];
  };

  // Check if language is supported
  const isLanguageSupported = (language: string): language is SupportedLanguage => {
    return ['en', 'pt'].includes(language);
  };

  return {
    // Current language state
    currentLanguage,
    languagePreference,
    isInitialized: !!currentLanguage,

    // Language methods
    changeLanguage: changeUserLanguage,
    syncLanguagePreference: syncLanguagePreferenceToFirestore,
    getAvailableLanguages,
    isLanguageSupported,

    i18n,
    t: i18n.t,
  };
}

/**
 * Hook for language switching functionality
 * Simplified version for components that only need to change language
 */
export function useLanguageSwitcher(): {
  currentLanguage: string;
  changeLanguage: (newLanguage: SupportedLanguage) => Promise<void>;
  availableLanguages: Array<{
    code: SupportedLanguage;
    name: string;
    nativeName: string;
  }>;
} {
  const { currentLanguage, changeLanguage, getAvailableLanguages } = useUserLanguage();

  return {
    currentLanguage,
    changeLanguage,
    availableLanguages: getAvailableLanguages(),
  };
}

/**
 * Hook for getting current language without change capabilities
 * Useful for components that only need to read the current language
 */
export function useCurrentLanguage(): {
  currentLanguage: string;
  t: TFunction;
} {
  const currentLanguage = useAtomValue(currentLanguageAtom);
  const { t } = useTranslation();

  return {
    currentLanguage,
    t,
  };
}
